/** Checks that need the whole model: geometry and engine results together (build plan §5.6). */
import type { ZoneInstance } from './zones';
import type { ProjectResults } from '../engine/assess';
import type { Ref } from '../engine/trace';
import type { ProjectDoc } from '../model/types';

export interface Lint {
  id: string;
  code: 'G-05' | 'G-06' | 'G-07' | 'G-08';
  severity: 'warning' | 'info';
  message: string;
  ref: Ref;
  /** Objects the lint is about, for focusing. */
  targets: string[];
}

/** Small-room threshold for G-06, m³. A heuristic, not from the standard. */
export const SMALL_ROOM_M3 = 50;

function shapeBounds(z: ZoneInstance): { min: [number, number, number]; max: [number, number, number] } | undefined {
  if (z.shape.kind !== 'lathe') return undefined;
  const r = Math.max(...z.shape.profile.map((p) => p[0]));
  const zs = z.shape.profile.map((p) => p[1]);
  const [x, y] = z.shape.axis;
  return { min: [x - r, y - r, Math.min(...zs)], max: [x + r, y + r, Math.max(...zs)] };
}

export function lintProject(doc: ProjectDoc, results: ProjectResults, zones: ZoneInstance[]): Lint[] {
  const lints: Lint[] = [];

  for (const s of doc.sources) {
    const r = results.sources[s.id];
    if (s.position[2] < 0) {
      lints.push({ id: `G-08:${s.id}`, code: 'G-08', severity: 'warning', message: `${s.tag} is ${(-s.position[2]).toFixed(1)} m below ground level.`, ref: { clause: '9' }, targets: [s.id] });
    }
    if (r?.dilution === 'low' && (s.grade === 'continuous' || s.grade === 'primary')) {
      lints.push({ id: `G-05:${s.id}`, code: 'G-05', severity: 'warning', message: `${s.tag} is a ${s.grade} source at low dilution. Relocate it, improve the ventilation or reduce the grade of release.`, ref: { clause: 'B.4' }, targets: [s.id] });
    }
  }

  for (const area of doc.areas) {
    if (area.kind !== 'indoor' || !area.box) continue;
    const [sx, sy, sz] = area.box.size;
    const volume = sx * sy * sz;
    const local = zones.filter((z) => z.areaId === area.id && z.shape.kind === 'lathe');
    const sourcesWithLocal = new Set(local.map((z) => z.sourceId));
    if (volume <= SMALL_ROOM_M3 && sourcesWithLocal.size >= 2) {
      lints.push({ id: `G-06:${area.id}`, code: 'G-06', severity: 'info', message: `${sourcesWithLocal.size} local zones in ${area.name} (${volume.toFixed(0)} m³). Consider classifying the whole room.`, ref: { clause: 'C.3.6.1' }, targets: [area.id] });
    }
    // G-07: a local zone reaching past the room walls (walls do not cut zones, MD-008)
    const [cx, cy, cz] = area.box.center;
    const c = Math.abs(Math.cos(area.box.yawRad));
    const s = Math.abs(Math.sin(area.box.yawRad));
    const hx = (c * sx + s * sy) / 2;
    const hy = (s * sx + c * sy) / 2;
    for (const z of local) {
      const b = shapeBounds(z);
      if (!b) continue;
      const out = b.min[0] < cx - hx - 1e-6 || b.max[0] > cx + hx + 1e-6 || b.min[1] < cy - hy - 1e-6 || b.max[1] > cy + hy + 1e-6 || b.max[2] > cz + sz / 2 + 1e-6;
      if (out) {
        const tag = doc.sources.find((x) => x.id === z.sourceId)?.tag ?? z.sourceId;
        lints.push({ id: `G-07:${z.id}`, code: 'G-07', severity: 'info', message: `The Zone ${z.zone} from ${tag} reaches past the walls of ${area.name}. Check walls and openings next to it.`, ref: { clause: '9', md: 'MD-008' }, targets: [z.sourceId, area.id] });
      }
    }
    for (const src of doc.sources.filter((x) => x.areaId === area.id)) {
      const [px, py, pz] = src.position;
      const inX = Math.abs(px - cx) <= hx + 1e-6 && Math.abs(py - cy) <= hy + 1e-6;
      const inZ = pz >= cz - sz / 2 - 1e-6 && pz <= cz + sz / 2 + 1e-6;
      if (!inX || !inZ) {
        lints.push({ id: `G-08:${src.id}:room`, code: 'G-08', severity: 'warning', message: `${src.tag} is assigned to ${area.name} but placed outside it.`, ref: { clause: '9' }, targets: [src.id, area.id] });
      }
    }
  }
  return lints;
}
